<template>
  <!-- Choices side by side (wrapping when there are many), the selected one filled, with no hover effect: the
       builder's Endings | Agreements switch, the Submissions page tabs and filters, the Design tab's sections.
       The chosen side is a fully rounded pill inside the padded track; with two choices it rounds only on its outer
       edge, so the two halves read as one split switch.
       v-locale-provider: Vuetify's rounded-s / rounded-e only apply inside a left-to-right parent. -->
  <v-locale-provider>
    <div class="segmented-toggle d-flex rounded-lg border border-grey-lighten-2 align-center pa-1"
      :class="[scrollable ? 'flex-nowrap overflow-x-auto' : 'flex-wrap', { 'w-100': block }]" role="tablist" :aria-label="label">
      <!-- block: equal-width sides that may shrink below their content (min-width 0), so a full-width
           toggle fits a phone screen instead of spilling out of its card. -->
      <v-btn v-for="(item, index) in items" :key="item.value" :min-width="block ? 0 : undefined" :class="[
        'text-none font-weight-bold', scrollable ? 'flex-shrink-0' : '', block ? 'flex-1-1-0' : 'flex-grow-1', small || block ? 'px-3' : 'px-5', roundedClass(index),
        item.value !== modelValue ? 'text-grey-darken-4' : tint ? `text-${item.color || color}` : 'text-white'
      ]" rounded="0" :height="small ? 30 : 36" :color="item.value === modelValue ? (item.color || color) : 'transparent'"
        :variant="item.value !== modelValue ? 'text' : tint ? 'tonal' : 'flat'" :size="small ? 'small' : 'default'" :ripple="false"
        role="tab" :prepend-icon="item.icon" :aria-selected="item.value === modelValue"
        @click="item.value !== modelValue && emit('update:modelValue', item.value)">
        {{ item.text }}
        <!-- A count beside the label (e.g. Sent 12): white on the solid chosen side, brand elsewhere. -->
        <v-chip v-if="item.count" size="x-small" rounded="pill" variant="tonal" class="ms-2 font-weight-medium"
          :color="item.value === modelValue && !tint ? 'white' : (item.color || color)">
          {{ item.count }}
        </v-chip>
      </v-btn>
    </div>
  </v-locale-provider>
</template>

<script setup>
const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  // [{ value, text, icon?, color?, count? }] — optional colour distinguishes filter categories; count shows a number chip.
  items: { type: Array, required: true },
  // Read out by screen readers, e.g. "Submissions view"
  label: { type: String, required: true },
  block: { type: Boolean, default: false },
  small: { type: Boolean, default: false },
  // Keep long parts lists on one row, accessible by horizontal scrolling.
  scrollable: { type: Boolean, default: false },
  // Chosen side light blue with blue text instead of solid blue: for a filter (Active | Archived)
  // that sits under a solid page switch.
  tint: { type: Boolean, default: false },
  // Theme colour of the chosen side
  color: { type: String, default: 'brand-color' }
})
const emit = defineEmits(['update:modelValue'])

// Two choices: each side rounds on its outer edge only. Otherwise every side is a full pill.
function roundedClass(index) {
  if (props.items.length !== 2) return 'rounded-lg'
  return index === 0 ? 'rounded-s-lg' : 'rounded-e-lg'
}
</script>

<style scoped>
/* No lift or shade on hover (the app's global button hover); Vuetify has no prop to turn it off. */
.segmented-toggle :deep(.v-btn:hover) {
  transform: none !important;
  box-shadow: none !important;
}

.segmented-toggle :deep(.v-btn:hover > .v-btn__overlay) {
  opacity: 0;
}
</style>
